import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Alert,
    Divider,
    InputAdornment,
    IconButton,
    Chip,
    MenuItem,
    alpha,
    useTheme,
} from '@mui/material';
import {
    Save as SaveIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    NetworkCheck as NetworkCheckIcon,
    Psychology as PsychologyIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import { adminApi } from '../services/api';

// ── Provider catalogue ────────────────────────────────────────────────────────

const PROVIDERS = [
    {
        id: 'gemini',
        label: 'Google Gemini',
        accentColor: '#4285f4',
        keyField: 'gemini_api_key',
        keyLabel: 'Gemini API Key',
        keyHint: 'Create a key at https://aistudio.google.com/app/apikey',
        freeField: 'gemini_model_free',
        premiumField: 'gemini_model_premium',
        freeDefault: 'gemini-flash-latest',
        premiumDefault: 'gemini-pro-latest',
        freeLabel: 'Free tier model',
        premiumLabel: 'Premium tier model',
        thumbnailNote: 'Thumbnail generation (Imagen) always uses Gemini regardless of active provider.',
    },
    {
        id: 'openai',
        label: 'OpenAI',
        accentColor: '#10a37f',
        keyField: 'openai_api_key',
        keyLabel: 'OpenAI API Key',
        keyHint: 'Create a key at https://platform.openai.com/api-keys',
        freeField: 'openai_model_free',
        premiumField: 'openai_model_premium',
        freeDefault: 'gpt-4o-mini',
        premiumDefault: 'gpt-4o',
        freeLabel: 'Free tier model',
        premiumLabel: 'Premium tier model',
    },
    {
        id: 'anthropic',
        label: 'Anthropic (Claude)',
        accentColor: '#c96442',
        keyField: 'anthropic_api_key',
        keyLabel: 'Anthropic API Key',
        keyHint: 'Create a key at https://console.anthropic.com/settings/keys',
        freeField: 'anthropic_model_free',
        premiumField: 'anthropic_model_premium',
        freeDefault: 'claude-haiku-4-5-20251001',
        premiumDefault: 'claude-sonnet-4-6',
        freeLabel: 'Free tier model',
        premiumLabel: 'Premium tier model',
        thumbnailNote: 'Thumbnail generation always uses Gemini Imagen — switch only affects text generation.',
    },
    {
        id: 'grok',
        label: 'xAI Grok',
        accentColor: '#1d9bf0',
        keyField: 'grok_api_key',
        keyLabel: 'Grok API Key',
        keyHint: 'Create a key at https://console.x.ai',
        freeField: 'grok_model_free',
        premiumField: 'grok_model_premium',
        freeDefault: 'grok-3-mini',
        premiumDefault: 'grok-3',
        freeLabel: 'Free tier model',
        premiumLabel: 'Premium tier model',
        thumbnailNote: 'Thumbnail generation always uses Gemini Imagen — switch only affects text generation.',
    },
];

const MASKED = '••••••••';

// ── Netflix-style provider card ───────────────────────────────────────────────

function ProviderCard({ provider, values, showKey, onToggleKey, onChange, isActive }) {
    const theme = useTheme();
    const accent = provider.accentColor;

    return (
        <Box sx={{
            position: 'relative',
            borderRadius: 3,
            overflow: 'hidden',
            border: `1px solid ${isActive ? accent : alpha(theme.palette.divider, 0.6)}`,
            background: `linear-gradient(145deg, ${alpha(accent, 0.06)} 0%, ${alpha(theme.palette.background.paper, 1)} 60%)`,
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 8px 24px ${alpha(accent, 0.15)}`,
            },
            // Bottom accent bar when active
            ...(isActive && {
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0, left: 0, right: 0,
                    height: 3,
                    background: accent,
                    borderRadius: '0 0 3px 3px',
                },
            }),
        }}>
            {/* Ambient glow blob */}
            <Box sx={{
                position: 'absolute', top: -30, right: -30,
                width: 120, height: 120,
                borderRadius: '50%',
                background: `radial-gradient(circle, ${alpha(accent, 0.12)}, transparent 70%)`,
                pointerEvents: 'none',
            }} />

            <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ color: accent }}>
                        {provider.label}
                    </Typography>
                    {isActive && (
                        <Chip label="Active" size="small" sx={{ bgcolor: alpha(accent, 0.15), color: accent, fontWeight: 700, border: `1px solid ${alpha(accent, 0.3)}` }} />
                    )}
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* API Key */}
                    <Box>
                        <TextField
                            fullWidth
                            label={provider.keyLabel}
                            value={values[provider.keyField] || ''}
                            onChange={e => onChange(provider.keyField, e.target.value)}
                            type={showKey ? 'text' : 'password'}
                            size="small"
                            placeholder="Leave blank to keep existing key"
                            inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={onToggleKey} edge="end"
                                            aria-label={showKey ? 'Hide key' : 'Show key'}>
                                            {showKey ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            {provider.keyHint}
                        </Typography>
                    </Box>

                    {/* Model names */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        <TextField
                            fullWidth
                            label={provider.freeLabel}
                            value={values[provider.freeField] || ''}
                            onChange={e => onChange(provider.freeField, e.target.value)}
                            size="small"
                            placeholder={provider.freeDefault}
                            inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
                            helperText={`Default: ${provider.freeDefault}`}
                        />
                        <TextField
                            fullWidth
                            label={provider.premiumLabel}
                            value={values[provider.premiumField] || ''}
                            onChange={e => onChange(provider.premiumField, e.target.value)}
                            size="small"
                            placeholder={provider.premiumDefault}
                            inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
                            helperText={`Default: ${provider.premiumDefault}`}
                        />
                    </Box>

                    {provider.thumbnailNote && isActive && (
                        <Alert severity="info" sx={{ py: 0.5, '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
                            {provider.thumbnailNote}
                        </Alert>
                    )}
                </Box>
            </Box>
        </Box>
    );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function AdminAISettings() {
    const [values, setValues] = useState({
        active_provider: 'gemini',
        gemini_api_key: '', gemini_model_free: '', gemini_model_premium: '',
        openai_api_key: '', openai_model_free: '', openai_model_premium: '',
        anthropic_api_key: '', anthropic_model_free: '', anthropic_model_premium: '',
        grok_api_key: '', grok_model_free: '', grok_model_premium: '',
    });
    const [showKeys, setShowKeys] = useState({
        gemini: false, openai: false, anthropic: false, grok: false,
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loadError, setLoadError] = useState('');
    const [connectionStatus, setConnectionStatus] = useState(null); // null | {connected, provider, model, message}

    const fetchConfig = useCallback(async () => {
        setLoadError('');
        setLoading(true);
        try {
            const data = await adminApi.getAIConfig();
            setValues(prev => ({ ...prev, ...data }));
        } catch (err) {
            setLoadError(err.message || 'Failed to load AI configuration');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchConfig(); }, [fetchConfig]);

    const handleChange = (field, value) => {
        setValues(prev => ({ ...prev, [field]: value }));
        setSuccess('');
        setConnectionStatus(null);
    };

    const handleSave = async () => {
        // Warn if the selected active provider has no key (neither a saved value nor a new one)
        const activeProvider = PROVIDERS.find(p => p.id === values.active_provider);
        if (activeProvider) {
            const keyVal = values[activeProvider.keyField];
            const hasKey = keyVal && keyVal !== '' && keyVal !== MASKED;
            // MASKED means there IS an existing key on the server — that's fine
            const hasExistingKey = keyVal === MASKED;
            if (!hasKey && !hasExistingKey) {
                setError(`Warning: "${activeProvider.label}" is set as the active provider but has no API key configured. Please enter an API key or choose a different provider.`);
                return;
            }
        }

        setSaving(true);
        setError('');
        setSuccess('');
        setConnectionStatus(null);
        try {
            // Omit placeholder masked values — backend will keep existing keys
            const payload = { ...values };
            PROVIDERS.forEach(p => {
                if (payload[p.keyField] === MASKED) delete payload[p.keyField];
            });
            await adminApi.updateAIConfig(payload);
            setSuccess('AI configuration saved successfully.');
            await fetchConfig();
        } catch (err) {
            setError(err.message || 'Failed to save AI configuration');
        } finally {
            setSaving(false);
        }
    };

    const handleTest = async () => {
        setTesting(true);
        setError('');
        setSuccess('');
        setConnectionStatus(null);
        try {
            const result = await adminApi.testAIConnection();
            setConnectionStatus(result);
            if (result.connected) {
                setSuccess(`${result.message}`);
            } else {
                setError(result.message || 'AI connection test failed. Check your API key and model name.');
            }
        } catch (err) {
            setConnectionStatus({ connected: false });
            setError(err.message || 'Connection test failed');
        } finally {
            setTesting(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (loadError) {
        return (
            <Box sx={{ p: 4, maxWidth: 720, mx: 'auto' }}>
                <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>
                <Button startIcon={<RefreshIcon />} onClick={fetchConfig}>Retry</Button>
            </Box>
        );
    }

    const activeProvider = values.active_provider || 'gemini';

    return (
        <Box sx={{ maxWidth: 860, mx: 'auto', p: 2 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <PsychologyIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                    <Box>
                        <Typography variant="h5" fontWeight={700}>AI Provider Settings</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Configure which AI provider powers content generation
                        </Typography>
                    </Box>
                </Box>
                {connectionStatus !== null && (
                    <Chip
                        label={connectionStatus.connected ? 'Connected' : 'Not Connected'}
                        color={connectionStatus.connected ? 'success' : 'error'}
                        size="small"
                    />
                )}
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>{success}</Alert>}

            {/* How it works info */}
            <Box sx={{ mb: 3, p: 2.5, borderRadius: 2, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>How AI providers work</Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5, fontSize: '0.85rem', color: 'text.secondary' } }}>
                    <li>Select an <strong>Active Provider</strong> below — all text generation (ideas, captions, analysis) will use that provider.</li>
                    <li>Each provider has a <strong>free tier model</strong> (used for free/creator plans) and a <strong>premium model</strong> (growth/pro/agency).</li>
                    <li>Thumbnail generation (Imagen) <strong>always uses Gemini</strong> regardless of the active text provider. Keep the Gemini API key configured.</li>
                    <li>API keys are stored encrypted in Firestore and never exposed to the browser after saving.</li>
                </Box>
            </Box>

            {/* Active provider selector */}
            <Box sx={{ mb: 3 }}>
                <TextField
                    select
                    label="Active Provider"
                    value={activeProvider}
                    onChange={e => handleChange('active_provider', e.target.value)}
                    size="small"
                    sx={{ minWidth: 220 }}
                    helperText="All text generation will use this provider"
                >
                    {PROVIDERS.map(p => (
                        <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>
                    ))}
                </TextField>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Provider cards grid */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, mb: 4 }}>
                {PROVIDERS.map(provider => (
                    <ProviderCard
                        key={provider.id}
                        provider={provider}
                        values={values}
                        showKey={showKeys[provider.id]}
                        onToggleKey={() => setShowKeys(prev => ({ ...prev, [provider.id]: !prev[provider.id] }))}
                        onChange={handleChange}
                        isActive={activeProvider === provider.id}
                    />
                ))}
            </Box>

            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                    variant="contained"
                    disableElevation
                    startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
                    onClick={handleSave}
                    disabled={saving || testing}
                >
                    Save Changes
                </Button>
                <Button
                    variant="outlined"
                    startIcon={testing ? <CircularProgress size={18} /> : <NetworkCheckIcon />}
                    onClick={handleTest}
                    disabled={saving || testing}
                >
                    Test Active Provider
                </Button>
            </Box>
        </Box>
    );
}
